import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivateFn, Params, RouterStateSnapshot, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PagePath } from '../enums/page-path.enum';
import type { Page } from '../models/app-config.model';
import type { PassParamsMap } from '../models/pass-parameter.model';
import { SpinnerEvent } from '../models/spinner.model';
import { AuthService } from '../services/auth.service';
import { ConfigResourceService } from '../services/config-resource.service';
import { DeepLinkingService } from '../services/deep-linking.service';
import { GuardsContextService } from '../services/guards-context.service';
import { NavigationUtilityService } from '../services/navigation-utility.service';
import { PassParameterService } from '../services/pass-parameter.service';
import { RoutesUtilityService } from '../services/routes-utility.service';
import { SpinnerService } from '../services/spinner.service';

// Checks if a user is authorized to access a certain site resource
// eslint-disable-next-line complexity
export const authGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  guardsContextService = inject(GuardsContextService),
  authService = inject(AuthService),
  navigationUtilityService = inject(NavigationUtilityService),
  passParameterService = inject(PassParameterService),
  deepLinkingService = inject(DeepLinkingService),
  spinnerService = inject(SpinnerService),
  configService = inject(ConfigResourceService)
): Promise<boolean | UrlTree> => {
  if (!authService.isCurrentTokenValid()) {
    await firstValueFrom(authService.loadJWT$());
  }

  const isDeepLinkingEnabled: boolean =
    configService.getProcessedResourceValue<string>('MDC6IsDeepLinkingEnabled', 'true')?.toLowerCase() === 'true';

  if (isDeepLinkingEnabled) {
    const passParams = passParameterService.createPassParamsFromDeepLinkingValues(route.queryParams);
    const routeContainsDeepLinkingKey = passParameterService.hasDeepLinkingPassParams(passParams);
    let passParametersMap: PassParamsMap | null = null;

    if (routeContainsDeepLinkingKey) {
      if (authService.isCurrentTokenValid()) {
        spinnerService.show({ event: SpinnerEvent.AUTHENTICATION_LOADING });

        const queryParams: Params = route.queryParams;
        const page: Page | null = navigationUtilityService.getPage(state.url);
        const pagePath: PagePath | null = RoutesUtilityService.convertPageNameToPagePath(page?.pageName);
        const pagePaths: PagePath[] = pagePath ? [pagePath] : [];

        passParametersMap = passParameterService.resolveParameters(pagePaths, queryParams);

        // Check if destination page is allowed. Redirect to home if not.
        if (!deepLinkingService.isDestinationPageAllowed(pagePaths, queryParams)) {
          const redirectToHomeUrlTree = deepLinkingService.redirectToHome();
          if (redirectToHomeUrlTree) {
            spinnerService.hide({ event: SpinnerEvent.AUTHENTICATION_LOADING });
            return navigationUtilityService.areRoutesEqual(redirectToHomeUrlTree, route) || redirectToHomeUrlTree;
          }
        }

        let urlTree: UrlTree | null = null;
        try {
          guardsContextService.setIsProcessingAuth(true);
          urlTree = await passParameterService.processParameters(passParametersMap);
        } finally {
          guardsContextService.setIsProcessingAuth(false);
        }

        if (urlTree) {
          spinnerService.hide({ event: SpinnerEvent.AUTHENTICATION_LOADING });
          return navigationUtilityService.areRoutesEqual(urlTree, route) || urlTree;
        } else {
          urlTree = await deepLinkingService.processPageSpecificParams(pagePath, passParametersMap);

          if (urlTree) {
            spinnerService.hide({ event: SpinnerEvent.AUTHENTICATION_LOADING });
            return navigationUtilityService.areRoutesEqual(urlTree, route) || urlTree;
          }
        }

        spinnerService.hide({ event: SpinnerEvent.AUTHENTICATION_LOADING });
      } else {
        return false;
      }
    }
  }

  return true;
};
